import { __decorate } from "tslib";
import React from 'react';
import { observer } from 'mobx-react';
import { action, observable } from 'mobx';
import defaultTo from 'lodash/defaultTo';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import TriggerField from '../trigger-field/TriggerField';
import autobind from '../_util/autobind';
import EventManager from '../_util/EventManager';
import { FieldType } from '../data-set/enum';
import { $l } from '../locale-context';
function getNodeRect(node) {
    return node.getBoundingClientRect();
}
let ColorPicker = class ColorPicker extends TriggerField {
    constructor() {
        super(...arguments);
        this.eventManager = new EventManager(typeof window === 'undefined' ? undefined : document);
        this.HSV = {
            h: 0,
            s: 1,
            v: 1,
            a: 1,
        };
        this.saveGradientRef = node => (this.gradient = node);
        this.saveSelectPointerRef = node => (this.selectPointer = node);
        this.saveHuePointerRef = node => (this.huePointer = node);
        this.saveHueRef = node => (this.hue = node);
        this.saveOpacityRef = node => (this.opacity = node);
        this.saveOpacityPointerRef = node => (this.opacityPointer = node);
    }
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('ColorPicker', label ? 'value_missing' : 'value_missing_no_label', {
                label,
            }),
            typeMismatch: $l('ColorPicker', 'type_mismatch'),
        };
    }
    componentDidUpdate() {
        const { popup } = this;
        if (popup) {
            const { h, s, v } = this.HSV;
            const { huePointer, selectPointer, hue, gradient } = this;
            if (huePointer && hue) {
                const { width } = getNodeRect(hue);
                this.setHuePointer((width * h) / 360, huePointer, hue, false);
            }
            if (selectPointer && gradient) {
                const { width, height } = getNodeRect(gradient);
                const left = s * width;
                const top = height - v * height;
                this.setGradientPointer(left, top, selectPointer, gradient, false);
            }
        }
    }
    syncValueOnBlur(value) {
        if (value !== '' && value[0] !== '#' && !value.startsWith('rgb') && !value.startsWith('hls')) {
            value = `#${value}`;
        }
        super.syncValueOnBlur(value);
    }
    getFieldType() {
        return FieldType.color;
    }
    getPrefix() {
        const { prefixCls } = this;
        return (React.createElement("div", { className: `${prefixCls}-prefix` },
            React.createElement("span", { className: `${prefixCls}-color`, style: { backgroundColor: this.getValue() } })));
    }
    getPopupFooter() {
        const { prefixCls } = this;
        const huePointerProps = {
            onMouseDown: this.handleHPMouseDown,
            ref: this.saveHuePointerRef,
            className: `${prefixCls}-popup-footer-slider-pointer`,
        };
        return (React.createElement("div", { className: `${prefixCls}-popup-footer` },
            React.createElement("div", { ref: this.saveHueRef, className: `${prefixCls}-popup-footer-slider` },
                React.createElement("div", { onClick: this.handleHueClick, className: "hue" }),
                React.createElement("div", Object.assign({}, huePointerProps))),
            React.createElement("div", { ref: this.saveOpacityRef, className: `${prefixCls}-popup-footer-slider opacity` },
                React.createElement("div", { ref: this.saveOpacityPointerRef, className: `${prefixCls}-popup-footer-slider-pointer` }))));
    }
    getPopupContent() {
        const { prefixCls } = this;
        const gradientProps = {
            className: `${prefixCls}-popup-body-gradient`,
            onClick: this.handleGPClick,
            ref: this.saveGradientRef,
        };
        const gradientPointerProps = {
            onMouseDown: this.handleGPMouseDown,
            ref: this.saveSelectPointerRef,
            className: `${prefixCls}-popup-body-selector`,
        };
        if (isEmpty(this.hueColor)) {
            this.setColor(this.getValue());
        }
        return (React.createElement("div", { className: `${prefixCls}-popup-view` },
            React.createElement("div", { className: `${prefixCls}-popup-body`, style: { backgroundColor: defaultTo(this.hueColor, '#ff0000') } },
                React.createElement("div", Object.assign({}, gradientProps)),
                React.createElement("div", Object.assign({}, gradientPointerProps))),
            this.getPopupFooter()));
    }
    setHSV(h, s, v, a) {
        const { HSV } = this;
        if (h !== undefined && h !== HSV.h) {
            HSV.h = h;
        }
        if (v !== undefined && v !== HSV.v) {
            HSV.v = v;
        }
        if (s !== undefined && s !== HSV.s) {
            HSV.s = s;
        }
        if (a !== undefined && a !== HSV.a) {
            HSV.a = a;
        }
    }
    setHueColor(color) {
        if (color !== this.hueColor) {
            this.hueColor = color;
        }
    }
    setColor(color) {
        if (!isNil(color) && color.slice(0, 1) === '#' && color.length > 3) {
            const { gradient, selectPointer, hue, huePointer } = this;
            const { r, g, b, a } = this.hexToRGB(color);
            const { h, s, v } = this.rgbToHSV(r / 255, g / 255, b / 255, a);
            this.setHSV(h, s, v, a);
            const { r: hr, g: hg, b: hb, a: ha } = this.hsvToRGB(h, 1, 1, 1);
            const hueColor = this.rgbToHEX(hr, hg, hb, ha);
            this.setHueColor(hueColor);
            if (gradient && selectPointer && hue && huePointer) {
                const { height, width } = getNodeRect(gradient);
                const left = s * width;
                const top = height - v * height;
                const { width: hueWidth } = getNodeRect(hue);
                const hueLeft = (h / 360) * hueWidth;
                this.setHuePointer(hueLeft, huePointer, hue, false);
                this.setGradientPointer(left, top, selectPointer, gradient, false);
            }
        }
    }
    positionToHSV(left, top, width, height) {
        const { h, a } = this.HSV;
        if (width < 0) {
            width = 0;
        }
        const s = left / width;
        const v = 1 - top / height;
        return { h, s, v, a };
    }
    rgbToHEX(r, g, b, a) {
        function hex(num) {
            const hexNum = num.toString(16);
            return hexNum.length === 1 ? `0${hexNum}` : hexNum;
        }
        if (a !== 1) {
            return `#${hex(r)}${hex(g)}${hex(b)}${hex((a * 255) / 10)}`;
        }
        return `#${hex(r)}${hex(g)}${hex(b)}`;
    }
    hexToRGB(hex) {
        hex = hex.split('#')[1] || hex.split('#')[0];
        const length = hex.length;
        let results = '';
        const hexArray = hex.split('');
        if (length === 3 || length === 4) {
            for (let i = 0; i < length; i++) {
                results = `${results}${hexArray[i]}${hexArray[i]}`;
            }
        }
        else if (length === 5) {
            results = `${hex}${hexArray[length - 1]}`;
        }
        else {
            results = hex;
        }
        results = results.slice(0, 6);
        const result = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(results);
        return result
            ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16),
                a: 1,
            }
            : {
                r: 255,
                g: 0,
                b: 0,
                a: 1,
            };
    }
    rgbToHSV(r, g, b, a) {
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const v = max;
        let s;
        let h;
        if (v === 0) {
            s = 0;
        }
        else {
            s = (max - min) / max;
        }
        if (max === min) {
            h = 0;
        }
        else {
            const d = r === min ? g - b : b === min ? r - g : b - r;
            const m = r === min ? 3 : b === min ? 1 : 5;
            h = 60 * (m - d / (max - min));
        }
        if (h < 0) {
            h += 360;
        }
        return { h, s, v, a };
    }
    hsvToRGB(h, s, v, a) {
        h /= 60;
        const h1 = Math.floor(h);
        const f = h - h1;
        const p = v * (1 - s);
        const q = v * (1 - f * s);
        const t = v * (1 - (1 - f) * s);
        let rgb;
        switch (h1) {
            case 0:
                rgb = { r: v, g: t, b: p, a };
                break;
            case 1:
                rgb = { r: q, g: v, b: p, a };
                break;
            case 2:
                rgb = { r: p, g: v, b: t, a };
                break;
            case 3:
                rgb = { r: p, g: q, b: v, a };
                break;
            case 4:
                rgb = { r: t, g: p, b: v, a };
                break;
            default:
                rgb = { r: v, g: p, b: q, a };
                break;
        }
        rgb.r = Math.floor(rgb.r * 255);
        rgb.g = Math.floor(rgb.g * 255);
        rgb.b = Math.floor(rgb.b * 255);
        return rgb;
    }
    setGradientPointer(x, y, pointer, wrap, isClient) {
        const { left: wrapX, top: wrapY, width: wrapW, height: wrapH } = getNodeRect(wrap);
        const { height: pointerH, width: pointerW } = getNodeRect(pointer);
        let left;
        let top;
        if (isClient) {
            left = x - wrapX < 0 ? 0 : x - wrapX > wrapW ? wrapW : x - wrapX;
            top = y - wrapY < 0 ? 0 : y - wrapY > wrapH ? wrapH : y - wrapY;
        }
        else {
            left = x;
            top = y;
        }
        pointer.style.left = `${left - pointerH / 2}px`;
        pointer.style.top = `${top - pointerW / 2}px`;
        return { left, top };
    }
    handleGPClick(e) {
        const { gradient, selectPointer, setGradientPointer } = this;
        if (gradient && selectPointer) {
            const { positionToHSV, rgbToHEX, hsvToRGB } = this;
            const { left, top } = setGradientPointer(e.clientX, e.clientY, selectPointer, gradient, true);
            const { height, width } = getNodeRect(gradient);
            const { h, s, v, a: opacity } = positionToHSV(left, top, width, height);
            this.setHSV(undefined, s, v, undefined);
            const { r, g, b, a } = hsvToRGB(h, s, v, opacity);
            const hexColor = rgbToHEX(r, g, b, a);
            this.prepareSetValue(hexColor);
        }
    }
    setHuePointer(x, pointer, wrap, isClient) {
        const { left: wrapX, width: wrapW } = getNodeRect(wrap);
        const { width: pointerW } = getNodeRect(pointer);
        let left;
        if (isClient) {
            left = x - wrapX < 0 ? 0 : x - wrapX > wrapW ? wrapW : x - wrapX;
        }
        else {
            left = x;
        }
        pointer.style.left = `${left - pointerW / 2}px`;
        if (left === wrapW) {
            return { left: 0, wrapW };
        }
        return { left, wrapW };
    }
    handleHueClick(e) {
        const { hue, huePointer, setHuePointer, hsvToRGB, rgbToHEX } = this;
        if (hue && huePointer) {
            const { left, wrapW } = setHuePointer(e.clientX, huePointer, hue, true);
            const h = Math.floor((left / wrapW) * 360);
            const { s, v, a: opacity } = this.HSV;
            this.setHSV(h, undefined, undefined, undefined);
            const { r, g, b, a } = hsvToRGB(h, 1, 1, 1);
            const { r: valueR, g: valueG, b: valueB, a: valueA } = hsvToRGB(h, s, v, opacity);
            const hueColor = rgbToHEX(r, g, b, a);
            const valueColor = rgbToHEX(valueR, valueG, valueB, valueA);
            this.setHueColor(hueColor);
            this.prepareSetValue(valueColor);
        }
    }
    handleGPMouseDown() {
        this.eventManager
            .addEventListener('mousemove', this.handleGPClick)
            .addEventListener('mouseup', this.onGPMouseUp);
    }
    onGPMouseUp() {
        this.eventManager
            .removeEventListener('mousemove', this.handleGPClick)
            .removeEventListener('mouseup', this.onGPMouseUp);
    }
    handleHPMouseDown() {
        this.eventManager
            .addEventListener('mousemove', this.handleHueClick)
            .addEventListener('mouseup', this.onHPMouseUp);
    }
    onHPMouseUp() {
        this.eventManager
            .removeEventListener('mousemove', this.handleHueClick)
            .removeEventListener('mouseup', this.onHPMouseUp);
    }
    handlePopupAnimateAppear() {
        this.setColor(this.getValue());
    }
    handlePopupAnimateEnd() {
        // noop
    }
    getPopupStyleFromAlign() {
        return undefined;
    }
    getTriggerIconFont() {
        return 'palette';
    }
    renderLengthInfo() {
        return undefined;
    }
};
ColorPicker.displayName = 'ColorPicker';
ColorPicker.defaultProps = {
    ...TriggerField.defaultProps,
    suffixCls: 'color-picker',
    clearButton: false,
};
__decorate([
    observable
], ColorPicker.prototype, "hueColor", void 0);
__decorate([
    action
], ColorPicker.prototype, "setHueColor", null);
__decorate([
    autobind
], ColorPicker.prototype, "setColor", null);
__decorate([
    autobind
], ColorPicker.prototype, "positionToHSV", null);
__decorate([
    action
], ColorPicker.prototype, "hexToRGB", null);
__decorate([
    autobind
], ColorPicker.prototype, "setGradientPointer", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleGPClick", null);
__decorate([
    autobind
], ColorPicker.prototype, "setHuePointer", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleHueClick", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleGPMouseDown", null);
__decorate([
    autobind
], ColorPicker.prototype, "onGPMouseUp", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleHPMouseDown", null);
__decorate([
    autobind
], ColorPicker.prototype, "onHPMouseUp", null);
__decorate([
    autobind
], ColorPicker.prototype, "handlePopupAnimateAppear", null);
ColorPicker = __decorate([
    observer
], ColorPicker);
export default ColorPicker;
